import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatTimestamp } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const ACTIONS = [["credit", "Top up"], ["debit", "Deduct"], ["set", "Set balance"]];

export const DriverWalletDialog = ({ driver, onClose, onChanged }) => {
  const [wallet, setWallet] = useState(null);
  const [action, setAction] = useState("credit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/drivers/${driver.user_id}/wallet`).then((r) => setWallet(r.data)).catch(() => toast.error("Could not load wallet"));
  useEffect(() => { load(); }, [driver.user_id]);

  const save = async () => {
    if (amount === "" || Number(amount) < 0) return toast.error("Enter a valid amount");
    if (!note.trim()) return toast.error("Add a reason/note");
    setBusy(true);
    try {
      const r = await api.post(`/admin/drivers/${driver.user_id}/wallet`, { action, amount: Number(amount), note });
      toast.success(`Wallet updated — new balance ${inr(r.data.balance)}`);
      setAmount(""); setNote("");
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="driver-wallet-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{driver.name} — Wallet</DialogTitle>
          <DialogDescription>Current balance: <span data-testid="driver-wallet-dialog-balance" className="font-bold text-slate-900">{wallet ? inr(wallet.balance) : "…"}</span></DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {ACTIONS.map(([k, label]) => (
            <button key={k} data-testid={`wallet-action-${k}`} onClick={() => setAction(k)}
              className={`flex-1 h-9 rounded-lg border text-sm font-medium transition-colors ${action === k ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-amber-300"}`}>{label}</button>
          ))}
        </div>
        <Input data-testid="wallet-amount-input" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={action === "set" ? "New balance (₹)" : "Amount (₹)"} />
        <Input data-testid="wallet-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note (required)" />
        <Button data-testid="wallet-save-button" onClick={save} disabled={busy} className="h-10 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save wallet change"}
        </Button>
        <WalletLog txns={wallet?.transactions || []} />
      </DialogContent>
    </Dialog>
  );
};

const WalletLog = ({ txns }) => (
  <div>
    <p className="text-sm font-semibold text-slate-700">Transaction log</p>
    <div data-testid="wallet-txn-log" className="mt-2 max-h-56 overflow-y-auto space-y-1.5">
      {txns.length === 0 && <p className="text-xs text-slate-400">No transactions yet</p>}
      {txns.map((t) => (
        <div key={t.id} data-testid="wallet-txn-row" className="rounded-lg border border-slate-100 px-3 py-2 text-xs">
          <div className="flex justify-between gap-2">
            <span className="text-slate-700 truncate">{t.reason}</span>
            <span className={`font-bold shrink-0 ${t.type === "credit" ? "text-emerald-600" : "text-red-600"}`}>{t.type === "credit" ? "+" : "−"}{inr(t.amount)}</span>
          </div>
          <p className="text-slate-400 mt-0.5">
            {formatTimestamp(t.created_at)} · Balance {inr(t.balance_after)}{t.source === "admin" ? ` · by ${t.admin_email || "admin"}` : ""}
          </p>
        </div>
      ))}
    </div>
  </div>
);
